<template>
  <v-data-table
    :search="search"
    :items-per-page="25"
    :items-per-page-options="[{ value: 25, title: '25' }]"
    :hover="vehiclesMakes.length > 0"
    item-value="name"
    :headers="vehicleMakeDataTableHeaders"
    :items="vehiclesMakes"
    :loading="loading"
    color="primary"
    sticky
    fixed-header
    density="default"
    height="calc(100% - 37px)"
    style="height: calc(100% - 93px); width: 100%"
    :row-props="generateRowProps"
  >
    <template #[`item.index`]="{ index }">
      {{ index + 1 }}
    </template>

    <template #[`item.actions`]="{ item }">
      <VehicleMakeDetailOptionsButton
        :vehicle-make="item"
        @vehicle-model-create-pressed="
          (vehicle: IVehicleMake) => openVehicleModelFormDialog(vehicle)
        "
        @vehicle-make-edit-pressed="
          (vehicle: IVehicleMake) => emit('vehicle-make-edit-pressed', vehicle)
        "
        @vehicle-make-delete-pressed="
          (vehicle: IVehicleMake) => emit('vehicle-make-delete-pressed', vehicle)
        "
      />
    </template>

    <template v-slot:no-data>
      <NoDataAvailablePlaceholder
        header="No vehicles types available"
        header-class="font-weight-medium text-h5"
      >
        <template #prepend>
          <v-card
            variant="flat"
            class="mask-bottom-card mb-3"
            color="transparent"
            width="180"
            height="120"
          >
            <v-list
              style="color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity))"
              variant="outlined"
              density="compact"
              lines="two"
              class="pt-0"
            >
              <v-list-item class="rounded-lg-xl mb-3 px-3">
                <template v-slot:prepend>
                  <v-avatar size="32" class="me-n1" color="green-darken-2 text-white">
                    JD
                  </v-avatar>
                </template>
                <v-skeleton-loader class="me-n3 ms-n4 my-n2" type="text, text" boilerplate />
              </v-list-item>
              <v-list-item class="rounded-lg-xl mb-3 px-3">
                <template v-slot:prepend>
                  <v-avatar size="32" class="me-n1" color="amber-darken-2 text-white">
                    DM
                  </v-avatar>
                </template>
                <v-skeleton-loader class="me-n3 ms-n4 my-n2" type="text, text" boilerplate />
              </v-list-item>
            </v-list>
          </v-card>
        </template>

        <template #description>
          <p class="mt-0 mb-6 text-caption text-medium-emphasis">
            <span v-if="currentUser?.hasPermission('fleet.add_vehiclemake')">
              Click
              <span class="text-primary">Create Vehicle Make</span> in the top right to create a
              vehicle make
            </span>
            <span v-else>
              You do not have the permission to create a vehicle type. Contact your administrator
              for more details
            </span>
          </p>
        </template>
      </NoDataAvailablePlaceholder>
    </template>
  </v-data-table>

  <VehicleModelFormDialog
    v-if="selectedVehicleMake"
    v-model:dialog="isVehicleModelFormDialogActive"
    :vehicle-make-id="selectedVehicleMake.id"
    :vehicle-model="selectedVehicleModel"
    :isEdit="!!selectedVehicleModel"
  />
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'

import { useAuthStore } from '@/stores'

import type { IVehicleMake, IVehicleModel } from '@/models/fleet-management'
import type { ReadonlyTableHeaders } from '@/utils/tables'

import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue'
import VehicleMakeDetailOptionsButton from '@/components/fleet-management/vehicles-types/VehicleMakeDetailOptionsButton.vue'
import VehicleModelFormDialog from '@/components/fleet-management/vehicles-types/VehicleModelFormDialog.vue'

import { ref } from 'vue'

interface Props {
  vehiclesMakes: IVehicleMake[]
  search: string
  loading: boolean
}

interface Emit {
  (e: 'vehicle-make-selected', vehicle: IVehicleMake): void
  (e: 'vehicle-make-edit-pressed', vehicle: IVehicleMake): void
  (e: 'vehicle-make-delete-pressed', vehicle: IVehicleMake): void
}

const emit = defineEmits<Emit>()
defineProps<Props>()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const vehicleMakeDataTableHeaders: ReadonlyTableHeaders = [
  {
    title: '#',
    align: 'start',
    key: 'index',
    width: 30,
    sortable: false
  },
  {
    title: 'Name',
    align: 'start',
    key: 'name',
    sortable: false
  },
  { title: '', key: 'actions', align: 'end', sortable: false }
]

const selectedVehicleModel = ref<IVehicleModel>()
const isVehicleModelFormDialogActive = ref(false)

const selectedVehicleMake = ref<IVehicleMake>()

function openVehicleModelFormDialog(vehicleMake: IVehicleMake, vehicleModel?: IVehicleModel) {
  selectedVehicleModel.value = vehicleModel
  selectedVehicleMake.value = vehicleMake

  isVehicleModelFormDialogActive.value = true
}

function generateRowProps(props: { index: number; item: IVehicleMake }) {
  return {
    // apply click listener to row props to open detail drawer
    onClick: () => emit('vehicle-make-selected', props.item)
  }
}
</script>
